import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { BlackFullWidthBtn } from '../../../shared/component/headless/button/BtnBody';
import { getModalMessage } from '../../../shared/constants/modalMessages';
import { getBtnName } from '../../../shared/constants/btnNames';
import { useProjectDetailState } from '../../../shared/hooks/useApi/useProjectDetail';
import { useProjectInfoState } from '../../../shared/hooks/useApi/useProjectInfo';
import { useProjectElementState } from '../../../shared/hooks/useApi/useProjectElement';
import DocumentProject from '../DocumentProject';
import { pdf } from '@react-pdf/renderer';
import { usePageOwnerIdentityStore } from '../../../shared/store/pageOwnerIdentityStore';
import { WorkViewerInfo } from '../../../shared/component/headless/input/InputBody';
import HeadlessInput from '../../../shared/component/headless/input/HeadlessInput';
import { AboutContentMode, TextAlignment } from '../../../shared/enum/EnumRepository';
import { PdfImageQuality, resolveProjectImageUrls } from '../../../shared/util/pdfImageUtils';
import belowIcon from '../../../asset/icon/below.png';
import { UniversalCheckbox } from '../../../shared/component/UniversalCheckbox';
import { getMemberInfo } from '../../../shared/api/memberInfoApi';
import { getCareerList } from '../../../shared/api/careerApi';
import { getAboutPageSetting } from '../../../shared/api/aboutPageSettingApi';
import { CareerData, MemberInfoData } from '../../../shared/dto/EntityRepository';
import { normalizeCareerList } from '../../../shared/util/normalizeCareer';
import { normalizeCareerSectionOrder } from '../../../shared/util/careerSectionOrder';
import { runCurrentArchiveRequest } from '../../../shared/util/currentArchiveRequest';
import { useSettingStore } from '../../../shared/store/settingStore';
import { shouldShowWorkArtist } from '../../../shared/util/workArtist';

const PDFProjectStationModalMobile: React.FC = () => {
  const { clearModal } = useModalStore();
  const { project } = useProjectDetailState();
  const { projectInfoList } = useProjectInfoState();
  const { projectElementList } = useProjectElementState();
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const setting = useSettingStore((state) => state.setting);
  const includeArtist = shouldShowWorkArtist(setting);
  const [title, setTitle] = useState(project?.title || '');
  const [username, setUsername] = useState(pageOwner?.handle || '');
  const [withCV, setWithCV] = useState(false);
  const [contentMode, setContentMode] = useState<AboutContentMode>(AboutContentMode.Orig);
  const [quality, setQuality] = useState<PdfImageQuality>('MEDIUM');
  const [resolvedUrls, setResolvedUrls] = useState<{
    imageUrlMap: Record<string, string>;
    gifFirstFrameMap: Record<string, string>;
  } | null>(null);

  const listKey = useMemo(
    () => projectElementList?.map((e) => e.id).join(',') ?? '',
    [projectElementList]
  );
  const projectElementListRef = useRef(projectElementList);
  projectElementListRef.current = projectElementList;

  useEffect(() => {
    const list = projectElementListRef.current;
    setResolvedUrls(null);
    if (!list?.length) {
      setResolvedUrls({ imageUrlMap: {}, gifFirstFrameMap: {} });
      return;
    }
    let cancelled = false;
    resolveProjectImageUrls(list, quality).then((result) => {
      if (!cancelled) setResolvedUrls(result);
    });
    return () => {
      cancelled = true;
    };
  }, [listKey, quality]);

  const handleClose = () => {
    clearModal(400);
  };

  const handleSubmit = async () => {
    if (!pageOwner) return;
    const { aui } = pageOwner;

    await runCurrentArchiveRequest(
      aui,
      async () => {
        const resolved =
          resolvedUrls ??
          (projectElementList?.length
            ? await resolveProjectImageUrls(projectElementList, quality)
            : { imageUrlMap: {}, gifFirstFrameMap: {} });

        let cvMemberInfo: MemberInfoData | null = null;
        let cvCareerList: CareerData[] = [];
        let careerSectionOrder = normalizeCareerSectionOrder(undefined);
        if (withCV) {
          try {
            const [miRes, clRes, aboutPageSettingRes] = await Promise.all([
              getMemberInfo(aui),
              getCareerList(aui),
              getAboutPageSetting(aui).catch(() => null),
            ]);
            cvMemberInfo = miRes.data;
            cvCareerList = normalizeCareerList(clRes.data ?? [])
              .filter((career) => career.isPublic);
            careerSectionOrder = normalizeCareerSectionOrder(
              aboutPageSettingRes?.data.careerSectionOrder,
            );
          } catch {
            cvMemberInfo = null;
            cvCareerList = [];
          }
        }

        const blob = await pdf(
          <DocumentProject
            title={title}
            username={username}
            includeArtist={includeArtist}
            projectInfoList={projectInfoList}
            projectElementList={projectElementList}
            resolvedImageUrlMap={resolved.imageUrlMap}
            resolvedGifFirstFrameMap={resolved.gifFirstFrameMap}
            includeCv={withCV}
            cvContentMode={contentMode}
            cvMemberInfo={cvMemberInfo}
            cvCareerList={cvCareerList}
            careerSectionOrder={careerSectionOrder}
          />
        ).toBlob();

        return {
          blob,
          fileName:
            withCV && contentMode === AboutContentMode.ENG
              ? `Project_${title}_CV_EN.pdf`
              : `Project_${title}.pdf`,
        };
      },
      ({ blob, fileName }) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(url);
        clearModal(400);
      },
    );
  };

  if (!project) return null;

  return (
    <PDFProjectStationComp>
      <TitleWrapper>
        <PDFProjectStationTitle>{getModalMessage('PDF_PROJECT_OPTIONS_TITLE')}</PDFProjectStationTitle>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent={true}
          backgroundImg={belowIcon}
          onClick={handleClose}
          aria-label="Close"
        />
      </TitleWrapper>
      <PDFContent>
        <WorkInfo>
          <WorkInfoRow>
            <WorkInfoLabel>Title</WorkInfoLabel>
            <HeadlessInput
              value={title}
              placeholder="Title"
              handleChange={(e) => setTitle(e.target.value)}
              StyledInput={WorkViewerInfo}
              $alignment={TextAlignment.LEFT}
            />
          </WorkInfoRow>
          <WorkInfoRow>
            <WorkInfoLabel>Username</WorkInfoLabel>
            <HeadlessInput
              value={username}
              placeholder="Username"
              handleChange={(e) => setUsername(e.target.value)}
              StyledInput={WorkViewerInfo}
              $alignment={TextAlignment.LEFT}
            />
          </WorkInfoRow>
          <WorkInfoRow>
            <WorkInfoLabel>{getModalMessage('PDF_PROJECT_CV_LABEL')}</WorkInfoLabel>
            <RadioGroup>
              <CheckboxComp>
                <UniversalCheckbox
                  type="radio"
                  name="pdfProjectCvIncludeMobile"
                  checked={withCV === false}
                  onChange={() => setWithCV(false)}
                />
                {getModalMessage('PDF_PROJECT_CV_WITHOUT')}
              </CheckboxComp>
              <CheckboxComp>
                <UniversalCheckbox
                  type="radio"
                  name="pdfProjectCvIncludeMobile"
                  checked={withCV === true}
                  onChange={() => setWithCV(true)}
                />
                {getModalMessage('PDF_PROJECT_CV_WITH')}
              </CheckboxComp>
            </RadioGroup>
          </WorkInfoRow>
          {withCV && (
            <WorkInfoRow>
              <WorkInfoLabel>{getModalMessage('PDF_PROJECT_CV_LANGUAGE_LABEL')}</WorkInfoLabel>
              <RadioGroup>
                <CheckboxComp>
                  <UniversalCheckbox
                    type="radio"
                    name="pdfProjectCvLanguageMobile"
                    checked={contentMode === AboutContentMode.Orig}
                    onChange={() => setContentMode(AboutContentMode.Orig)}
                  />
                  {getModalMessage('EXPORT_LANGUAGE_ORIGINAL')}
                </CheckboxComp>
                <CheckboxComp>
                  <UniversalCheckbox
                    type="radio"
                    name="pdfProjectCvLanguageMobile"
                    checked={contentMode === AboutContentMode.ENG}
                    onChange={() => setContentMode(AboutContentMode.ENG)}
                  />
                  {getModalMessage('EXPORT_LANGUAGE_ENGLISH')}
                </CheckboxComp>
              </RadioGroup>
            </WorkInfoRow>
          )}
          <WorkInfoRow>
            <WorkInfoLabel>Quality</WorkInfoLabel>
            <RadioGroup>
              {(['LOW', 'MEDIUM', 'HIGH'] as PdfImageQuality[]).map((value) => (
                <CheckboxComp key={value}>
                  <UniversalCheckbox
                    type="radio"
                    name="pdfProjectQualityMobile"
                    checked={quality === value}
                    onChange={() => setQuality(value)}
                  />
                  {value[0] + value.slice(1).toLowerCase()}
                </CheckboxComp>
              ))}
            </RadioGroup>
          </WorkInfoRow>
        </WorkInfo>
      </PDFContent>
      <HeadlessBtn
        value={getBtnName('GENERATE')}
        handleClick={handleSubmit}
        StyledBtn={SubmitButton}
      />
    </PDFProjectStationComp>
  );
};

const PDFProjectStationComp = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  max-height: 95vh;
`;

const TitleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  margin-top: clamp(12px, 2vh, 30px);
  margin-bottom: clamp(20px, 8vh, 30px);
`;

const PDFProjectStationTitle = styled.h2`
  ${({ theme }) => theme.typography.Body_01_1};
  color: ${({ theme }) => theme.colors.color_Gray_02};
`;


const PDFContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  padding-bottom: 20px;
`;

const WorkInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const WorkInfoRow = styled.div`
  min-height: 18px;
  margin-bottom: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const WorkInfoLabel = styled.span`
  min-width: 70px;
  padding-top: 2px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const RadioGroup = styled.div`
  display: flex;
  padding-top: 2px;
  gap: 12px;
`;

const CheckboxComp = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 20px 0px 15px 0px;
`;

export default PDFProjectStationModalMobile;
